import { msg } from "@lit/localize";
import { signal } from "@preact/signals-core";
import { getAuthMe, postAuthLogout, postAuthMagicLink } from "@/api";
import { client } from "@/api/client.gen";
import type { DtoUserResponse } from "@/api";
import { hydrateWorkspaces } from "@/store/workspaces";
import { unsubscribePush } from "@/store/push";

export interface AuthState {
  user: DtoUserResponse | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export const auth = signal<AuthState>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
});

/** Apply a /auth/me-shaped response to auth + workspace signals. */
function applyUser(user: DtoUserResponse | null): void {
  auth.value = {
    user,
    isLoading: false,
    isAuthenticated: user != null,
  };
  if (user) {
    hydrateWorkspaces(user.workspaces, user.active_org_id);
  }
}

/** True when the signed-in user is an org Business Owner (sees every project). */
export function isBusinessOwner(): boolean {
  return auth.value.user?.is_business_owner ?? false;
}

/**
 * GET /auth/me plus its HTTP status (0 = no response). The SDK rejects with
 * only the error body (main.ts's interceptor rethrows even without
 * throwOnError), yet only the status tells "signed out" (401) apart from "API
 * restarting" (502), so it is read off the response on the way through.
 */
export async function probeAuthMe(): Promise<
  { status: number; user: DtoUserResponse | null }
> {
  let status = 0;
  const baseFetch = client.getConfig().fetch ?? globalThis.fetch;
  try {
    const { data } = await getAuthMe({
      throwOnError: true,
      fetch: (async (input: Request) => {
        const res = await baseFetch(input);
        status = res.status;
        return res;
      }) as typeof fetch,
    });
    return { status, user: data ?? null };
  } catch {
    return { status, user: null };
  }
}

/** Boot retries before giving up and showing the login page (~15 s). */
const BOOT_RETRIES = 5;
let bootAttempts = 0;

export async function fetchMe(): Promise<void> {
  const { status, user } = await probeAuthMe();
  if (user) {
    bootAttempts = 0;
    return applyUser(user);
  }
  // Only the API refusing the session (401, or another 4xx) signs out.
  if (status >= 400 && status < 500) return applyUser(null);
  // A 5xx (502 while the API restarts) or no response says nothing about the
  // session: keep it, and retry while the app is still booting. A server that
  // never answers (wrong API URL, CORS) ends on the login page, as before.
  if (!auth.value.isLoading) return;
  if (++bootAttempts > BOOT_RETRIES) return applyUser(null);
  setTimeout(() => void fetchMe(), 3000);
}

const NEXT_KEY = "post_login_next";

/**
 * A post-sign-in redirect target if it is safe to follow: a same-origin path.
 * Rejects "//host" and "/\host", which browsers resolve to another origin, so
 * a crafted ?next= cannot bounce users off-site.
 */
export function safeNextPath(v: string | null | undefined): string | null {
  if (!v || !v.startsWith("/") || v[1] === "/" || v[1] === "\\") return null;
  return v;
}

/**
 * Remember where to land once the emailed link is redeemed: ?next= (set when
 * a session expired) or the deep link the login page was shown on.
 * sessionStorage, like the pending email: the link usually opens in this tab.
 */
function rememberNextPath(): void {
  const { pathname, search } = window.location;
  // Invite flow (requested from the callback page): keep what was saved.
  if (pathname === "/auth/callback") return;
  const next = safeNextPath(new URLSearchParams(search).get("next")) ??
    (pathname === "/login" ? null : safeNextPath(pathname + search));
  if (next) window.sessionStorage.setItem(NEXT_KEY, next);
  else window.sessionStorage.removeItem(NEXT_KEY);
}

/** The remembered post-sign-in path (consumed), or "/". */
export function takeNextPath(): string {
  const next = safeNextPath(window.sessionStorage.getItem(NEXT_KEY));
  window.sessionStorage.removeItem(NEXT_KEY);
  return next ?? "/";
}

/**
 * Ask the backend to email a passwordless sign-in link.
 *
 * Always resolves when the request was accepted, even if the address is not
 * allowed to sign in: the backend deliberately answers the same either way so
 * the UI cannot be used to discover which addresses are registered.
 *
 * `inviteToken` (from an invite link) is carried into the emailed link so
 * redeeming it also accepts the invite.
 */
export async function requestMagicLink(
  email: string,
  inviteToken = "",
): Promise<void> {
  // Remembered so the callback can still redeem the link if Firebase drops
  // the address from the redirect URL.
  window.sessionStorage.setItem("pending_magic_link_email", email);
  rememberNextPath();
  await postAuthMagicLink({
    body: {
      email,
      ...(inviteToken ? { invite_token: inviteToken } : {}),
    },
    throwOnError: true,
  });
}

/** Redeem the one-time code from a sign-in link and start a session. */
export async function verifyMagicLink(
  email: string,
  oobCode: string,
  inviteToken = "",
): Promise<void> {
  const { error } = await client.post({
    url: "/auth/magic-link/verify",
    body: {
      email,
      oob_code: oobCode,
      ...(inviteToken ? { invite_token: inviteToken } : {}),
    },
    headers: { "Content-Type": "application/json" },
  });
  if (error) throw error;
  // Confirm the session against the server instead of trusting the verify
  // response body. When the browser refuses the __Host-token cookie (SPA and
  // API on different sites while COOKIE_SAME_SITE is lax, or a plain-HTTP hop
  // in the flow), applying the user here would mark the app authenticated
  // with no credential: app-shell then fires every startup request and each
  // one 401s, and the WebSocket upgrade is refused.
  await fetchMe();
  if (!auth.value.isAuthenticated) {
    throw new Error(
      msg(
        "Signed in, but your browser did not keep the session cookie. Contact your administrator if this keeps happening.",
      ),
    );
  }
}

export async function logout(): Promise<void> {
  try {
    // Drop this browser's push subscription while the session can still
    // authorise it: the backend keys subscriptions on (user, endpoint), so
    // keeping it would show this user's notifications to the next user here.
    await unsubscribePush().catch(() => {});
    await postAuthLogout({ throwOnError: true });
  } finally {
    // Full page navigation, not a soft route change. Module-level stores
    // (chat messages, task caches, sidebar data) and app-shell one-shot
    // flags (#sidebarLoaded/#themeLoaded/#wsConnected) would otherwise
    // leak the previous user's data into the next session on this tab.
    window.location.assign("/login");
  }
}
